import React, { memo, FunctionComponent } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Panel } from "@xfrontend/xui-react-plus";
import { RootState } from "#/types/RootState";
import ReactScrollbarsCustom from "./ReactScrollbarsCustom";
import ErrorBoundary from "./ErrorBoundary";

type PanelType = {
  children: React.ReactElement | React.ReactElement[];
  style?: React.CSSProperties | null;
  scrollbar?: boolean | string;
  className?: string;
  innerClassName?: string;
};

const PanelProvider: FunctionComponent<PanelType> = ({
  children,
  style,
  scrollbar,
  className,
  innerClassName,
}): JSX.Element => {
  const { appHeight, headerHeight } = useSelector((state: RootState) => state.infra);
  const height = (appHeight || 0) - (headerHeight || 0);
  const space = 12;
  const providerStyle = { height };
  const panelStyle = {
    height: `calc(100% - ${space}px)`,
    overflow: "auto",
    padding: "0px",
    ...style,
  };
  const wrapperStyle = { padding: `${space}px` };
  const Comp = !scrollbar ? React.Fragment : ReactScrollbarsCustom;
  return (
    <div className={`panel-provider ${className}`} style={providerStyle}>
      <Panel className={innerClassName} style={panelStyle}>
        <ErrorBoundary>
          <Comp style={wrapperStyle}>
            {children}
          </Comp>
        </ErrorBoundary>
      </Panel>
    </div>
  );
};

PanelProvider.defaultProps = {
  style: {},
  scrollbar: false,
  className: "",
  innerClassName: "",
};

PanelProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element.isRequired),
    PropTypes.element,
  ]).isRequired,
  style: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  scrollbar: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
  innerClassName: PropTypes.string,
};

PanelProvider.displayName = "PanelProvider";

export default memo(PanelProvider);
